import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: API_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password, rememberMe = false) => {
    const response = await api.post('/auth/login', { email, password, remember_me: rememberMe });
    const { access_token, user: userData, requires_2fa } = response.data;
    
    if (requires_2fa) {
      // Return the data for 2FA verification step
      return { access_token, user: userData, requires_2fa: true };
    }
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return { access_token, user: userData, requires_2fa: false };
  };

  const verify2FA = async (tempToken, code, rememberMe = false) => {
    const response = await api.post('/auth/2fa/verify', { 
      temp_token: tempToken, 
      code, 
      remember_me: rememberMe 
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return { access_token, user: userData };
  };

  const setup2FA = async () => {
    const response = await api.post('/auth/2fa/setup');
    return response.data;
  };

  const enable2FA = async (code) => {
    const response = await api.post('/auth/2fa/enable', { code });
    // Refresh user data
    await fetchUser();
    return response.data;
  };

  const disable2FA = async (code) => {
    const response = await api.post('/auth/2fa/disable', { code });
    // Refresh user data
    await fetchUser();
    return response.data;
  };

  const get2FAStatus = async () => {
    const response = await api.get('/auth/2fa/status');
    return response.data;
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    verify2FA,
    setup2FA,
    enable2FA,
    disable2FA,
    get2FAStatus,
    register,
    logout,
    api,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isHouseManager: user?.role === 'house_manager',
    isWorker: user?.role === 'worker',
    isFamily: user?.role === 'family'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
